import SEO from '../components/SEO';
import { directMailSchema, organizationSchema, breadcrumbSchema } from '../utils/structuredData';
import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Target, TrendingUp, Users, CheckCircle, ArrowRight, BarChart } from 'lucide-react';

const DirectMailPage = () => {
  return (
    <div style={styles.page}>
      <SEO
        title="Direct Mail Marketing for Dealerships — 3× Industry Response Rates"
        description="High-response direct mail campaigns for car, golf cart, motorcycle, and RV dealerships. Premium design, precision targeting, and response rates 3× the industry average. Drive qualified buyers to your next sales event."
        keywords="direct mail marketing dealerships, dealership direct mail campaign, automotive direct mail, car dealer mailer, golf cart dealer direct mail, high response direct mail, dealership event direct mail"
        canonicalUrl="https://driven2win.com/services/direct-mail"
        structuredData={[directMailSchema, organizationSchema, breadcrumbSchema([{name:'Home',url:'https://driven2win.com'},{name:'Services',url:'https://driven2win.com/services'},{name:'Direct Mail',url:'https://driven2win.com/services/direct-mail'}])]}
      />
      <style>{`
        @media (max-width: 768px) {
          .subpage-hero-h1 { font-size: 26px !important; letter-spacing: 0.5px !important; word-break: break-word !important; overflow-wrap: break-word !important; hyphens: auto !important; }
          .subpage-hero-sub { font-size: 15px !important; }
          .subpage-hero { padding: 60px 0 !important; }
          .subpage-section-h2 { font-size: 24px !important; }
          .subpage-section-pad { padding: 60px 0 !important; }
          .subpage-cta-h2 { font-size: 24px !important; }
          .subpage-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroOverlay} />
        <div style={styles.container}>
          <h1 style={styles.heroH1} className="subpage-hero-h1">
            DIRECT MAIL <span style={{color: '#FFC300'}}>MARKETING</span>
          </h1>
          <p style={styles.heroSubheadline}>
            Mailers That Create URGENCY, Drive MASSIVE RESPONSE, and Fill Your Event With Qualified Buyers
          </p>
        </div>
      </section>

      {/* Overview */}
      <section style={styles.overviewSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            FORGET BORING POSTCARDS. <span style={{color: '#0080FF'}}>THIS IS DIRECT MAIL THAT WORKS.</span>
          </h2>
          <p style={styles.bodyText}>
            Most direct mail gets tossed in the trash without a second glance. Generic offers, weak copy, no urgency — it's BORING and ineffective.
          </p>
          <p style={styles.bodyText}>
            <strong>Our mailers are different.</strong>
          </p>
          <p style={styles.bodyText}>
            We create HIGH-IMPACT campaigns that grab attention, create FOMO (fear of missing out), and drive recipients to your event with COMPELLING offers and URGENT messaging. Our mailers look like MAJOR EVENT announcements — not spam.
          </p>
          <p style={styles.bodyText}>
            <strong>Result?</strong> Mailboxes to event attendance. Phone calls. Appointments booked. Traffic through the door. Real buyers ready to deal.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section style={styles.howSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            HOW WE DRIVE <span style={{color: '#0080FF'}}>MASSIVE RESPONSE</span>
          </h2>
          
          <div style={styles.processGrid}>
            <ProcessStep
              icon={<Target />}
              title="Targeted List Development"
              description="We identify high-potential buyers in your market using demographic data, purchase history, and geographic targeting. No wasted mailers to non-buyers."
            />
            <ProcessStep
              icon={<Mail />}
              title="Compelling Design & Copy"
              description="Eye-catching designs with bold headlines, clear offers, and URGENT calls-to-action. Our mailers DEMAND attention and drive immediate response."
            />
            <ProcessStep
              icon={<TrendingUp />}
              title="Multi-Touch Campaigns"
              description="We send multiple mailers leading up to your event — building anticipation and maximizing response rates. Repetition = Results."
            />
            <ProcessStep
              icon={<BarChart />}
              title="Trackable Results"
              description="Every campaign includes response tracking so you know exactly what ROI you're getting. We measure calls, appointments, and event attendance."
            />
          </div>
        </div>
      </section>

      {/* What Makes Our Mailers Work */}
      <section style={styles.whyWorksSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            WHAT MAKES OUR MAILERS <span style={{color: '#0080FF'}}>CRUSH IT</span>
          </h2>
          
          <div style={styles.whyWorksGrid}>
            <WhyWorksCard
              title="URGENCY & SCARCITY"
              description="Limited-time offers, countdown timers, 'first 50 customers' messaging. We create FOMO that drives immediate action."
            />
            <WhyWorksCard
              title="IRRESISTIBLE OFFERS"
              description="Trade-in bonuses, financing incentives, exclusive event-only deals. Our offers are SO GOOD people can't ignore them."
            />
            <WhyWorksCard
              title="EYE-CATCHING DESIGN"
              description="Bold colors, large headlines, high-quality images. Our mailers STAND OUT in a pile of boring bills and junk mail."
            />
            <WhyWorksCard
              title="CLEAR CALL-TO-ACTION"
              description="'Call NOW for appointment!' 'Visit us THIS WEEKEND ONLY!' No confusion about what to do next — we tell them EXACTLY where to go and when."
            />
          </div>
        </div>
      </section>

      {/* Campaign Types */}
      <section style={styles.campaignSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            CAMPAIGN <span style={{color: '#0080FF'}}>TYPES</span>
          </h2>
          
          <div style={styles.campaignGrid}>
            <CampaignCard
              title="Event Announcement Mailers"
              description="Big, bold announcements that create BUZZ for your upcoming sales event. We make it look like THE EVENT OF THE YEAR."
              features={[
                "Full-color oversized postcards",
                "Event dates, location, and offers",
                "Multiple mailings (teaser + reminder)",
                "QR codes for instant appointment booking"
              ]}
            />
            <CampaignCard
              title="Trade-In Value Campaigns"
              description="Personalized mailers with estimated trade-in values that get recipients excited about upgrading their vehicle."
              features={[
                "Targeted to specific vehicle owners",
                "Pre-calculated trade-in estimates",
                "Financing pre-approval messaging",
                "Direct appointment scheduling"
              ]}
            />
            <CampaignCard
              title="VIP Invitation Mailers"
              description="Exclusive 'invite-only' campaigns that make recipients feel special and create urgency to attend."
              features={[
                "Premium paper stock and finishing",
                "'VIP Access' positioning",
                "Limited quantity messaging",
                "Special incentives for VIP attendees"
              ]}
            />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section style={styles.statsSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            OUR <span style={{color: '#FFFFFF'}}>NUMBERS</span>
          </h2>
          
          <div className="grid-4col" style={styles.statsGrid}>
            <StatCard number="2-4%" label="Average Response Rate" />
            <StatCard number="10K-80K" label="Mailers Per Campaign" />
            <StatCard number="200-1500+" label="Appointments Generated" />
            <StatCard number="5-10x" label="Return On Investment" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <h2 style={styles.ctaH2}>
            Ready to <span style={{color: '#FFC300'}}>FILL YOUR EVENT</span> With Qualified Buyers?
          </h2>
          <p style={styles.ctaText}>
            Let's create a direct mail campaign that drives MASSIVE response and packs your lot!
          </p>
          <Link to="/contact#contact-form" style={styles.btnPrimary}>
            Get Your FREE Direct Mail Quote
            <ArrowRight size={24} style={{marginLeft: '12px'}} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Components
const ProcessStep = ({ icon, title, description }) => (
  <div style={styles.processStep}>
    <div style={styles.processIcon}>
      {React.cloneElement(icon, { size: 48, color: '#0080FF' })}
    </div>
    <h3 style={styles.processTitle}>{title}</h3>
    <p style={styles.processDescription}>{description}</p>
  </div>
);

const WhyWorksCard = ({ title, description }) => (
  <div style={styles.whyWorksCard}>
    <h3 style={styles.whyWorksTitle}>{title}</h3>
    <p style={styles.whyWorksDescription}>{description}</p>
  </div>
);

const CampaignCard = ({ title, description, features }) => (
  <div style={styles.campaignCard}>
    <h3 style={styles.campaignTitle}>{title}</h3>
    <p style={styles.campaignDescription}>{description}</p>
    <div style={styles.campaignFeatures}>
      {features.map((feature, index) => (
        <div key={index} style={styles.campaignFeature}>
          <CheckCircle size={18} color="#0080FF" style={{flexShrink: 0}} />
          <span>{feature}</span>
        </div>
      ))}
    </div>
  </div>
);

const StatCard = ({ number, label }) => (
  <div style={styles.statCard}>
    <div style={styles.statNumber}>{number}</div>
    <div style={styles.statLabel}>{label}</div>
  </div>
);

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
  },
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg, #0080FF 0%, #0060CC 100%)',
    padding: '100px 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '3px',
    position: 'relative',
    zIndex: 1,
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '24px',
    color: '#FFFFFF',
    maxWidth: '900px',
    margin: '0 auto',
    lineHeight: '1.6',
    position: 'relative',
    zIndex: 1,
    fontWeight: '600',
  },
  overviewSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  sectionH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '48px',
    color: '#1A1F3A',
    marginBottom: '32px',
    letterSpacing: '1px',
    textAlign: 'center',
  },
  bodyText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px',
    lineHeight: '1.8',
    color: '#333',
    marginBottom: '20px',
    maxWidth: '900px',
    margin: '0 auto 20px',
  },
  howSection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  processGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    gap: '32px',
  },
  processStep: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '40px 32px',
    textAlign: 'center',
    border: '3px solid #E5E5E5',
  },
  processIcon: {
    marginBottom: '20px',
  },
  processTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#1A1F3A',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  processDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
  },
  whyWorksSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  whyWorksGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    gap: '32px',
  },
  whyWorksCard: {
    background: 'linear-gradient(135deg, #0080FF 0%, #0060CC 100%)',
    borderRadius: '16px',
    padding: '40px 32px',
    color: '#FFFFFF',
  },
  whyWorksTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  whyWorksDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
  },
  campaignSection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  campaignGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
    gap: '32px',
  },
  campaignCard: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '40px 32px',
    border: '3px solid #0080FF',
    borderTop: '8px solid #0080FF',
  },
  campaignTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#0080FF',
    marginBottom: '16px',
    letterSpacing: '1px',
  },
  campaignDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
    marginBottom: '24px',
  },
  campaignFeatures: {},
  campaignFeature: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '12px',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#333',
  },
  statsSection: {
    background: 'linear-gradient(135deg, #1A1F3A 0%, #2C2C2C 100%)',
    padding: '100px 0',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '32px',
  },
  statCard: {
    textAlign: 'center',
  },
  statNumber: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '56px',
    fontWeight: '700',
    color: '#FFC300',
    marginBottom: '8px',
  },
  statLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
  },
  ctaSection: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    padding: '100px 0',
    textAlign: 'center',
  },
  ctaH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  ctaText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#FFFFFF',
    marginBottom: '48px',
    fontWeight: '600',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#FFFFFF',
    color: '#E31E24',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    padding: '20px 48px',
    border: 'none',
    borderRadius: '8px',
    textDecoration: 'none',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
};

export default DirectMailPage;
